import React, { useState, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { uploadTodosCsv, getSuppliers } from '../services/api';

const parseCurrency = (str) => {
  if (str === undefined || str === null) return null;
  const cleaned = String(str).replace(/[$€£,\s]/g, '');
  if (cleaned === '') return null;
  const val = parseFloat(cleaned);
  return isNaN(val) ? null : val;
};

const normalizeMonthYear = (dateStr) => {
  // Accept DD/MM/YYYY or MM/YYYY or M/YYYY
  const parts = String(dateStr).trim().split('/');
  if (parts.length === 3) {
    const [dd, mm, yyyy] = parts;
    const month = mm.padStart(2, '0');
    return `${month}/${yyyy}`;
  }
  if (parts.length === 2) {
    const [mm, yyyy] = parts;
    return `${String(mm).padStart(2, '0')}/${yyyy}`;
  }
  return null;
};

const BatchTodoUpload = ({ open, onClose, onSuccess }) => {
  const [csvData, setCsvData] = useState(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [supplierEmails, setSupplierEmails] = useState(new Set());
  const fileInputRef = useRef(null);

  // Load suppliers for email validation on first open
  React.useEffect(() => {
    const load = async () => {
      try {
        const res = await getSuppliers();
        const emails = new Set(res.data.map(s => String(s.email).toLowerCase()));
        setSupplierEmails(emails);
      } catch (e) {
        // ignore; errors shown on use elsewhere
      }
    };
    if (open) load();
  }, [open]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      setErrors(['Please select a valid CSV file']);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvContent = e.target.result;
      parseCSV(csvContent);
    };
    reader.readAsText(file);
  };

  const parseCSV = (csvContent) => {
    const lines = csvContent.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) {
      setErrors(['CSV file is empty']);
      return;
    }
    const headers = lines[0].split(',').map(h => h.trim());

    const supplierNameKey = headers.find(h => h.toLowerCase().includes('supplier')) || 'Supplier Name';
    const emailKey = headers.find(h => h.toLowerCase().includes('email')) || 'Email';
    const dateKey = headers.find(h => h.toLowerCase().includes('date')) || 'Date';
    const titleKey = headers.find(h => h.toLowerCase().includes('title')) || 'Title';
    const originalAmountKey = headers.find(h => h.toLowerCase().includes('original')) || 'Original Amount';
    const deductionKey = headers.find(h => h.toLowerCase().includes('deduction')) || 'Deduction';
    const finalAmountKey = headers.find(h => h.toLowerCase().includes('final')) || 'Final Amount';

    const outRows = [];
    const newErrors = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const values = line.split(',');
      const row = {};
      headers.forEach((h, idx) => { row[h] = (values[idx] || '').trim(); });

      const supplierName = row[supplierNameKey]?.trim();
      const email = row[emailKey]?.trim().toLowerCase();
      const date = row[dateKey]?.trim();
      const title = row[titleKey]?.trim();
      const originalAmount = parseCurrency(row[originalAmountKey]);
      const deduction = parseCurrency(row[deductionKey]) || 0;
      const finalAmount = parseCurrency(row[finalAmountKey]);

      if (!supplierName) newErrors.push(`Line ${i + 1}: Supplier Name is required`);
      if (!email) newErrors.push(`Line ${i + 1}: Email is required`);
      if (email && !email.includes('@')) newErrors.push(`Line ${i + 1}: Invalid Email format`);
      if (!date) newErrors.push(`Line ${i + 1}: Date is required`);
      if (!title) newErrors.push(`Line ${i + 1}: Title is required`);
      if (originalAmount === null) newErrors.push(`Line ${i + 1}: Original Amount is required/invalid`);
      if (finalAmount === null) newErrors.push(`Line ${i + 1}: Final Amount is required/invalid`);

      const monthYear = normalizeMonthYear(date);
      if (!monthYear) newErrors.push(`Line ${i + 1}: Date must be DD/MM/YYYY or MM/YYYY`);

      // Validate email belongs to a supplier
      const emailExists = email ? supplierEmails.has(email) : false;

      outRows.push({
        supplierName,
        email,
        date,
        title,
        originalAmount,
        deduction,
        finalAmount,
        monthYear,
        emailExists,
      });
    }

    setRows(outRows);
    setSelected(outRows.map((_, idx) => idx));
    setErrors(newErrors);
    setCsvData(csvContent);
  };

  const handleSelectAll = (event) => {
    setSelected(event.target.checked ? rows.map((_, i) => i) : []);
  };

  const toggleRow = (idx) => {
    setSelected(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
  };

  const handleUpload = async () => {
    const selectedRows = selected.map(i => rows[i]);
    // Block upload if any selected row has missing supplier email in system
    const invalid = selectedRows.filter(r => !r.emailExists);
    if (invalid.length > 0) {
      setErrors([`Cannot upload: ${invalid.length} selected row(s) have emails not found in suppliers.`]);
      return;
    }

    setIsUploading(true);
    setErrors([]);

    try {
      // Transform into backend expected CSV:
      // headers: supplier_name,month-year,amount,deduction,total_after_deduction
      const headers = ['supplier_name', 'month-year', 'amount', 'deduction', 'total_after_deduction'];
      const lines = [headers.join(',')];
      selectedRows.forEach(r => {
        const line = [
          r.supplierName,
          r.monthYear?.replace('/', '-'),
          String(r.originalAmount),
          String(r.deduction ?? 0),
          String(r.finalAmount)
        ].join(',');
        lines.push(line);
      });
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const formData = new FormData();
      formData.append('file', blob, 'todos.csv');
      const res = await uploadTodosCsv(formData);
      setUploadResult(res.data);
      if (res.data?.tasks_created > 0) onSuccess?.();
    } catch (e) {
      setErrors([e.response?.data?.detail || e.message || 'Upload failed']);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setCsvData(null);
    setRows([]);
    setSelected([]);
    setErrors([]);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose?.();
  };

  const downloadTemplate = () => {
    const template = `Supplier Name,Email,Date,Title,Original Amount,Deduction,Final Amount\nRodela IT Park,rodelaitpark@gmail.com,31/05/2025,Monthly Invoice,43391.24,318.49,43072.75`;
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'todos_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const numInvalidSupplierEmails = useMemo(() => rows.filter(r => !r.emailExists).length, [rows]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } }}
    >
      <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, borderBottom: '1px solid #f0f0f0', pb: 2 }}>
        Batch Upload Tasks (Todos)
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {!csvData ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>Upload CSV File</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Required: Supplier Name, Email, Date, Title, Original Amount, Deduction, Final Amount
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
              <input accept=".csv" type="file" onChange={handleFileSelect} ref={fileInputRef} style={{ display: 'none' }} />
              <Button variant="contained" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()} sx={{ borderRadius: '8px', textTransform: 'none' }}>Choose CSV File</Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate} sx={{ borderRadius: '8px', textTransform: 'none' }}>Download Template</Button>
            </Box>
            {errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>{errors.map((e, i) => (<div key={i}>{e}</div>))}</Alert>
            )}
          </Box>
        ) : uploadResult ? (
          <Box>
            <Alert severity={uploadResult.errors && uploadResult.errors.length > 0 ? 'warning' : 'success'} sx={{ mb: 3 }}>
              <Typography variant="h6">{uploadResult.message}</Typography>
              <Typography variant="body2">Tasks created: {uploadResult.tasks_created || 0}</Typography>
            </Alert>
            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'rgba(211,47,47,0.05)' }}>
                <Typography variant="subtitle1" color="error" gutterBottom>Errors:</Typography>
                {uploadResult.errors.map((e, i) => (<Typography key={i} variant="body2" color="error">{e}</Typography>))}
              </Paper>
            )}
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Preview Tasks ({rows.length} found)</Typography>
              <FormControlLabel control={
                <Checkbox checked={selected.length === rows.length} indeterminate={selected.length > 0 && selected.length < rows.length} onChange={handleSelectAll} />
              } label="Select All" />
            </Box>
            {errors.length > 0 && (<Alert severity="error" sx={{ mb: 2 }}>{errors.map((e, i) => (<div key={i}>{e}</div>))}</Alert>)}
            {numInvalidSupplierEmails > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>{numInvalidSupplierEmails} row(s) have emails not found among suppliers; these cannot be uploaded.</Alert>
            )}
            <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell align="right">Original</TableCell>
                    <TableCell align="right">Deduction</TableCell>
                    <TableCell align="right">Final</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selected.includes(idx)} onChange={() => toggleRow(idx)} />
                      </TableCell>
                      <TableCell>{r.supplierName}</TableCell>
                      <TableCell>
                        {r.email}
                        {!r.emailExists && (
                          <Typography variant="caption" color="error" display="block">Unknown supplier email</Typography>
                        )}
                      </TableCell>
                      <TableCell>{r.date}</TableCell>
                      <TableCell>{r.title}</TableCell>
                      <TableCell align="right">{r.originalAmount != null ? `$${r.originalAmount.toFixed(2)}` : '—'}</TableCell>
                      <TableCell align="right">{r.deduction != null ? `$${Number(r.deduction).toFixed(2)}` : '$0.00'}</TableCell>
                      <TableCell align="right">{r.finalAmount != null ? `$${r.finalAmount.toFixed(2)}` : '—'}</TableCell>
                      <TableCell>
                        <Chip icon={r.emailExists ? <CheckIcon/> : <ErrorIcon/>} size="small" label={r.emailExists ? 'Ready' : 'Blocked'} color={r.emailExists ? 'success' : 'warning'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">{selected.length} of {rows.length} tasks selected</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" onClick={() => { setCsvData(null); setRows([]); setSelected([]); setErrors([]); if (fileInputRef.current) fileInputRef.current.value=''; }} sx={{ borderRadius: '8px', textTransform: 'none' }}>Upload Different File</Button>
                <Button variant="contained" onClick={handleUpload} disabled={isUploading || selected.length === 0} startIcon={isUploading ? <CircularProgress size={20}/> : <UploadIcon/>} sx={{ borderRadius: '8px', textTransform: 'none' }}>{isUploading ? 'Uploading...' : `Upload ${selected.length} Tasks`}</Button>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>{uploadResult ? 'Close' : 'Cancel'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchTodoUpload;
